/*
 * Turns a stack of Acuity schedules into tabs — one visible at a time.
 *
 * WHY. A schedule sizes itself to its whole content: Queens' class calendar is
 * 6119px, about seven screens. Stack a workshop calendar under that and the
 * workshop is buried past six screens of classes, which is the same as not
 * being there. Tabs let each one run as long as it needs without the other
 * paying for it.
 *
 * PROGRESSIVE, not decorative. The Liquid renders both schedules as plain
 * sections with their own headings, which is a correct, usable page on its own.
 * This upgrades that to tabs, and every ARIA role is added HERE rather than in
 * the markup — a `role="tabpanel"` with no working tablist is a worse lie to a
 * screen reader than no roles at all.
 *
 * It does nothing with fewer than two schedules, which is 88 of the 90 stores
 * that run classes. Only 176 and 187 have a workshop.
 *
 * Hiding a panel uses `hidden`, i.e. `display: none`, and that turns out to be
 * exactly what a lazy iframe wants: a hidden one is never fetched, so the
 * workshop calendar costs nothing until someone asks for it. On reveal it
 * loads, its ResizeObserver fires, and acuity-frame.js sizes it like any other.
 */
(function () {
  var KEYS = { LEFT: 37, RIGHT: 39, HOME: 36, END: 35 };

  function select(tabs, panels, index) {
    for (var i = 0; i < tabs.length; i++) {
      var on = i === index;
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      /* Only the active tab is reachable by Tab; the others are reached with
         the arrow keys, which is the tablist convention. */
      tabs[i].setAttribute('tabindex', on ? '0' : '-1');
      tabs[i].classList.toggle('is-active', on);
      panels[i].hidden = !on;
    }
  }

  function init(container) {
    if (container.__scheduleTabs) return;

    var panels = container.querySelectorAll('[data-schedule]');
    if (panels.length < 2) return;
    container.__scheduleTabs = true;

    var list = document.createElement('div');
    list.className = 'schedule-tabs';
    list.setAttribute('role', 'tablist');
    if (container.getAttribute('data-schedules-label')) {
      list.setAttribute('aria-label', container.getAttribute('data-schedules-label'));
    }

    var tabs = [];
    for (var i = 0; i < panels.length; i++) {
      var panel = panels[i];
      var id = panel.id || ('schedule-panel-' + i + '-' + Math.random().toString(36).slice(2, 8));
      panel.id = id;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '0');

      var tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'schedule-tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);
      tab.id = id + '-tab';
      panel.setAttribute('aria-labelledby', tab.id);
      /* The label comes off the panel rather than out of a setting here, so a
         store page and the classes page can word it differently without this
         file knowing either of them exists. */
      tab.textContent = panel.getAttribute('data-schedule-label')
        || panel.getAttribute('data-schedule');

      list.appendChild(tab);
      tabs.push(tab);
    }

    /* A page can give the tabs a home outside the container, such as the
       store page's banner. Otherwise they go at the top of it. */
    var host = document.getElementById(container.getAttribute('data-schedules-tabs-into') || '');
    var slot = host && host.querySelector('[data-schedule-tabs-slot]');
    if (slot) {
      slot.appendChild(list);
      host.classList.add('has-tabs');
    } else {
      container.insertBefore(list, container.firstChild);
    }
    container.classList.add('is-tabbed');
    var anchor = slot ? host : container;

    /* The host also learns which schedule is showing, so it can change with it
       (the store page's banner swaps its heading and photo). */
    function choose(index) {
      select(tabs, panels, index);
      if (slot) host.setAttribute('data-active-schedule', panels[index].getAttribute('data-schedule'));
    }

    for (var j = 0; j < tabs.length; j++) {
      (function (index) {
        tabs[index].addEventListener('click', function () {
          choose(index);
        });
      }(j));
    }

    list.addEventListener('keydown', function (event) {
      var current = tabs.indexOf(document.activeElement);
      if (current === -1) return;

      var next = null;
      if (event.keyCode === KEYS.RIGHT) next = (current + 1) % tabs.length;
      else if (event.keyCode === KEYS.LEFT) next = (current - 1 + tabs.length) % tabs.length;
      else if (event.keyCode === KEYS.HOME) next = 0;
      else if (event.keyCode === KEYS.END) next = tabs.length - 1;
      if (next === null) return;

      event.preventDefault();
      choose(next);
      tabs[next].focus();
    });

    /* Open the tab the URL names (#workshop, #classes), so a link or redirect
       can land on the workshop calendar rather than the first tab. */
    function fromHash() {
      var hash = window.location.hash.slice(1);
      if (!hash) return -1;
      for (var k = 0; k < panels.length; k++) {
        if (panels[k].id === hash) return k;
      }
      return -1;
    }

    var start = fromHash();
    choose(start === -1 ? 0 : start);
    if (start > 0) {
      anchor.scrollIntoView({ block: 'start' });
      /* The browser does its own jump to the #id once the page has loaded,
         which lands on the panel below the tabs, under the sticky header.
         Put the tabs back in view after it. */
      window.addEventListener('load', function () {
        setTimeout(function () { anchor.scrollIntoView({ block: 'start' }); }, 0);
      });
    }

    window.addEventListener('hashchange', function () {
      var index = fromHash();
      if (index === -1) return;
      choose(index);
      anchor.scrollIntoView({ block: 'start' });
    });
  }

  function initAll(root) {
    var containers = (root || document).querySelectorAll('[data-schedules]');
    for (var i = 0; i < containers.length; i++) init(containers[i]);
  }

  /* Exposed because the classes page builds its schedules in JavaScript after a
     shopper picks a store, long after this has run. */
  window.TandyScheduleTabs = { init: init, initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
}());
